import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AccessTime,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

const statusChipPalette = {
  Success: { bgLight: '#DCFCE7', text: '#166534', border: '#22C55E' },
  Failed: { bgLight: '#FEE2E2', text: '#991B1B', border: '#EF4444' },
  Running: { bgLight: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' },
  Scheduled: { bgLight: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  Pending: { bgLight: '#E5E7EB', text: '#374151', border: '#9CA3AF' },
  Processing: { bgLight: '#E9D5FF', text: '#6B21A8', border: '#A855F7' },
  Active: { bgLight: '#D1FAE5', text: '#064E3B', border: '#10B981' },
  Error: { bgLight: '#FEE2E2', text: '#991B1B', border: '#F87171' },
  Cancelled: { bgLight: '#F3F4F6', text: '#374151', border: '#9CA3AF' },
  'Partial Success': { bgLight: '#FFEDD5', text: '#9A3412', border: '#FB923C' },
  Unknown: { bgLight: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
};

const runModeChipPalette = {
  Scheduled: { bgLight: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  'Unknown Mode': { bgLight: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  hover: {
    scale: 1.01,
    backgroundColor: 'rgba(79, 172, 254, 0.05)',
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
};

const formatUtc = (dt) => dt || '';

function ScheduledStatusTable({ scheduledStatus }) {
  if (!scheduledStatus?.status) return null;

  const getStatusStyles = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return { label: 'Success', color: '#22C55E', icon: <AccessTime fontSize="small" sx={{ color: '#166534' }} /> };
      case 'FAILED':
        return { label: 'Failed', color: '#EF4444', icon: <AccessTime fontSize="small" sx={{ color: '#991B1B' }} /> };
      case 'RUNNING':
        return { label: 'Running', color: '#3B82F6', icon: <AccessTime fontSize="small" sx={{ color: '#1E40AF' }} /> };
      case 'PENDING':
        return { label: 'Pending', color: '#9CA3AF', icon: <AccessTime fontSize="small" sx={{ color: '#374151' }} /> };
      case 'PROCESSING':
        return { label: 'Processing', color: '#A855F7', icon: <AccessTime fontSize="small" sx={{ color: '#6B21A8' }} /> };
      case 'ACTIVE':
        return { label: 'Active', color: '#10B981', icon: <AccessTime fontSize="small" sx={{ color: '#064E3B' }} /> };
      case 'ERROR':
        return { label: 'Error', color: '#F87171', icon: <AccessTime fontSize="small" sx={{ color: '#991B1B' }} /> };
      case 'CANCELLED':
        return { label: 'Cancelled', color: '#9CA3AF', icon: <AccessTime fontSize="small" sx={{ color: '#374151' }} /> };
      case 'PARTIAL_SUCCESS':
        return { label: 'Partial Success', color: '#FB923C', icon: <AccessTime fontSize="small" sx={{ color: '#9A3412' }} /> };
      case 'SCHEDULED':
        return { label: 'Scheduled', color: '#F59E0B', icon: <AccessTime fontSize="small" sx={{ color: '#92400E' }} /> };
      default:
        return { label: 'Unknown', color: '#9CA3AF', icon: <AccessTime fontSize="small" sx={{ color: '#6B7280' }} /> };
    }
  };

  const getRunModeStyles = () => ({
    label: 'Scheduled',
    color: '#F59E0B',
    icon: <AccessTime fontSize="small" sx={{ color: '#92400E' }} />,
  });

  const statusStyle = getStatusStyles(scheduledStatus?.status);
  const runModeStyle = getRunModeStyles();

  const sched = scheduledStatus?.schedule || {};
  const startDateText = formatUtc(sched?.start_date);
  const endDateText = formatUtc(sched?.end_date);
  const nextDateText = formatUtc(sched?.next_run_at);
  const frequencyText = typeof sched?.frequency === 'string' ? sched.frequency : '';
  const timeOfDayText = typeof sched?.time_of_day === 'string' ? sched.time_of_day : '';
  const timezoneText = sched?.timezone || '';
  const message = scheduledStatus?.message || '';

  const statusPalette = statusChipPalette[statusStyle?.label] || statusChipPalette['Unknown'];
  const runModePalette = runModeChipPalette[runModeStyle?.label] || runModeChipPalette['Unknown Mode'];

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        borderRadius: '0px 0px 4px 4px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
        border: '1px solid rgba(89, 101, 166, 0.35)',
        p: 2,
        mb: 2,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `
      0px 4px 12px rgba(89, 101, 166, 0.15),
      0px 8px 24px rgba(89, 101, 166, 0.12),
      0px 1px 3px rgba(89, 101, 166, 0.2)
    `,
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(135deg, rgba(89,101,166,0.06) 0%, rgba(89,101,166,0.03) 100%)',
          zIndex: 0,
        },
      }}
    >
      {message && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '8px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #93C5FD',
            color: '#1E40AF',
            fontWeight: 500,
            fontSize: '0.95rem',
          }}
        >
          ℹ️ {message}
        </Box>
      )}

      <TableContainer
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(79, 172, 254, 0.1)',
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              position: 'relative',
              background: 'linear-gradient(135deg, #ececf2 0%, #dedfe6 100%)',
              boxShadow:
                'inset 0 -1px 0 rgba(89,101,166,0.25), 0 1px 6px rgba(2,8,23,.05)',
              transform: 'translateY(0)',
              transition:
                'background .25s ease, box-shadow .25s ease, transform .15s ease',
              '& th': {
                color: '#1f2230',
                fontWeight: 600,
                transition:
                  'transform .2s ease, letter-spacing .2s ease, color .2s ease',
              },
              '& th:not(:last-of-type)': {
                borderRight: '1px solid rgba(89,101,166,0.25)',
              },
              '&:hover th': {
                transform: 'scale(1.02)',
                letterSpacing: '0.01em',
                color: '#111827',
              },
            }}
          >
            <TableRow>
              {[
                'Start Date',
                'End Date',
                'Next Date',
                'Frequency',
                'Status',
                'Run Mode',
                'Time of Day',
                'Timezone',
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    py: 0.6,
                    color: '#374151',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <motion.tr
              variants={rowVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              component={TableRow}
              sx={{
                fontSize: '0.875rem',
                '&:nth-of-type(even)': {
                  backgroundColor: 'rgba(79, 172, 254, 0.02)',
                },
              }}
            >
              {/* Start Date */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
                {startDateText && <span>{startDateText}</span>}
              </TableCell>

              {/* End Date */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
                {endDateText && <span>{endDateText}</span>}
              </TableCell>

              {/* Next Date */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
                {nextDateText && <span>{nextDateText}</span>}
              </TableCell>

              {/* Frequency */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#92400E', py: 2, fontWeight: 600 }}>
                {frequencyText && <span>{frequencyText}</span>}
              </TableCell>

              {/* Status */}
              <TableCell sx={{ py: 2 }}>
                <Chip
                  icon={statusStyle?.icon}
                  label={statusStyle?.label}
                  size="small"
                  sx={{
                    backgroundColor: statusPalette.bgLight,
                    color: statusPalette.text,
                    border: `1px solid ${statusPalette.border}`,

                    fontWeight: 700,
                    height: 28,
                    borderRadius: '8px',
                    px: 1.2,
                    boxShadow:
                      'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                    '& .MuiChip-icon': {
                      color: `${statusPalette.text} !important`,
                      opacity: 1,
                      mr: 0.75,
                    },
                    '& .MuiChip-icon svg': { fontSize: 25 },
                    '& .MuiChip-label': {
                      px: 0.5,
                      letterSpacing: '0.02em',
                      fontWeight: 700,
                    },
                  }}
                />
              </TableCell>

              {/* Run Mode */}
              <TableCell sx={{ py: 2 }}>
                <Chip
                  icon={runModeStyle?.icon}
                  label={runModeStyle?.label}
                  size="small"
                  sx={{
                    backgroundColor: runModePalette.bgLight,
                    color: runModePalette.text,
                    border: `1px solid ${runModePalette.border}`,

                    fontWeight: 700,
                    height: 28,
                    borderRadius: '10px',
                    px: 1.2,
                    boxShadow:
                      'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                    '& .MuiChip-icon': {
                      color: `${runModePalette.text} !important`,
                      opacity: 1,
                      mr: 0.75,
                    },
                    '& .MuiChip-icon svg': { fontSize: 25 },
                    '& .MuiChip-label': {
                      px: 0.5,
                      letterSpacing: '0.02em',
                      fontWeight: 700,
                    },
                  }}
                />
              </TableCell>

              {/* Time of Day */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
                {timeOfDayText && <span>{timeOfDayText}</span>}
              </TableCell>

              {/* Timezone */}
              <TableCell sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
                {timezoneText && <span>{timezoneText}</span>}
              </TableCell>
            </motion.tr>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

ScheduledStatusTable.propTypes = {
  scheduledStatus: PropTypes.object,
};

export default ScheduledStatusTable;